import { Button, Collapse, Empty, Space, Spin, Tag } from "antd";
import { FilePdfOutlined, RightOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { FactoryReportEntry } from "@audit-plan/shared";
import { download } from "../../api/client";
import { useFactoryReports } from "../../api/factories";
import { useEvidenceList } from "../../api/evidence";
import { AuditStatusPill, OutcomePill, Rating, fmtD, fmtN } from "../pills";
import { OriginLabel } from "../audit/AuditTable";
import { EvidencePanel } from "../evidence/EvidencePanel";

/** Contenuto della riga aperta: Report PDF del tool, evidenze per ruolo (stesso elenco della pagina audit), Apri audit. */
function AuditRowBody({ e, supplierId }: { e: FactoryReportEntry; supplierId: string }) {
  const { t } = useTranslation();
  const ev = useEvidenceList({ auditId: e.auditId });
  return (
    <Space direction="vertical" style={{ width: "100%" }} size={10}>
      <Space wrap>
        {e.generatedReport && (
          <Button icon={<FilePdfOutlined />} onClick={() => download(`/audits/${e.auditId}/report.pdf`)}>
            {t("f_toolReport")}
          </Button>
        )}
        <Link to={`/audits/${e.auditId}`}>
          <Button icon={<RightOutlined />}>{t("f_openAudit")}</Button>
        </Link>
      </Space>
      <div style={{ fontWeight: 600 }}>
        {t("evidence")} ({e.evidenceCount})
      </div>
      {ev.isLoading ? <Spin /> : <EvidencePanel bare evidence={ev.data ?? []} ctx={{ supplierId, auditId: e.auditId }} canUpload={false} onChanged={() => void ev.refetch()} />}
    </Space>
  );
}

/** Scheda «Audit» dello stabilimento: una riga per audit, dal più recente, che si apre su report ed evidenze. */
export function FactoryAudits({ factoryId, supplierId }: { factoryId: string; supplierId: string }) {
  const { t } = useTranslation();
  const q = useFactoryReports(factoryId);
  if (q.isLoading) return <Spin style={{ display: "block", margin: 24 }} />;
  if (!q.data?.length) return <Empty description={t("f_noAudits")} />;
  return (
    <Collapse
      destroyOnHidden
      items={q.data.map((e) => ({
        key: e.auditId,
        label: (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <b>{fmtD(e.date)}</b>
            <span style={{ fontFamily: "var(--ap-mono)" }}>{e.code}</span>
            {e.historical ? <Tag color="purple">{t("f_historical")}</Tag> : <AuditStatusPill a={{ status: e.status as never, outcome: null }} />}
            <OriginLabel a={{ origin: e.origin as "program" | "client", client: e.client }} />
            <span style={{ opacity: 0.65 }}>{e.supplierName}</span>
            <span style={{ marginInlineStart: "auto", display: "inline-flex", gap: 12, alignItems: "center" }}>
              <span>
                {t("f_conformity")}:{" "}
                {e.conformity != null ? (
                  <span>
                    <Rating score={e.conformity} override={e.ratingOverride} /> <span className="num">{fmtN(e.conformity, 1)}{e.historical ? "" : "%"}</span>
                  </span>
                ) : e.outcome === "pass" || e.outcome === "fail" ? (
                  <OutcomePill outcome={e.outcome} />
                ) : (
                  "—"
                )}
              </span>
              <Tag style={{ marginInlineEnd: 0 }}>
                {t("evidence")} {e.evidenceCount}
              </Tag>
            </span>
          </div>
        ),
        children: <AuditRowBody e={e} supplierId={supplierId} />,
      }))}
    />
  );
}
